import React, { useEffect, useState, useCallback } from 'react';
import Logo from '../assets/images/logo.png';
import { useTranslation } from 'react-i18next';

interface ServiceUnavailableProps {
  onRetry?: () => void;
}

const RETRY_INTERVAL_MS = 30000; // 30 seconds

const ServiceUnavailable: React.FC<ServiceUnavailableProps> = ({ onRetry }) => {
  const { t } = useTranslation();

  const [isChecking, setIsChecking] = useState(false);

  // Health check function
  const checkApiHealth = useCallback(async () => {
    setIsChecking(true);
    try {
      const graphqlEndpoint =
        import.meta.env.VITE_GRAPHQL_ENDPOINT || 'http://localhost:3100/graphql';

      // Simple introspection query to check if API is responding
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: '{ __typename }',
        }),
      });

      if (response.ok) {
        // API is back online - reload the page
        window.location.reload();
        return true;
      }
      return false;
    } catch {
      // API still unavailable
      return false;
    } finally {
      setIsChecking(false);
    }
  }, []);

  // Manual retry handler
  const handleManualRetry = useCallback(() => {
    if (onRetry) {
      onRetry();
    } else {
      checkApiHealth();
    }
  }, [onRetry, checkApiHealth]);

  // Auto-retry effect with 30-second interval
  useEffect(() => {
    // Initial check after component mounts
    const initialTimeout = setTimeout(() => {
      checkApiHealth();
    }, RETRY_INTERVAL_MS);

    // Set up recurring health checks
    const healthCheckInterval = setInterval(() => {
      checkApiHealth();
    }, RETRY_INTERVAL_MS);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(healthCheckInterval);
    };
  }, [checkApiHealth]);

  return (
    <div className="min-h-screen bg-ds-bg flex flex-col items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {/* Logo */}
        <div className="mb-8">
          <img src={Logo} alt={t('directHomeService')} className="h-[75px] w-auto mx-auto" />
        </div>

        {/* Message */}
        <div className="bg-white rounded-ds-default shadow-ds-card p-8">
          <div className="text-6xl mb-4">🔧</div>
          <h1 className="text-2xl font-bold text-ds-text-primary mb-4">
            {t('serviceUnavailable')}
          </h1>
          <p className="text-ds-text-secondary mb-6">{t('serviceUnavailableMessage')}</p>

          {/* Auto-retry status - Hidden per requirement, but logic runs in background */}

          {/* Contact Info */}
          <div className="bg-ds-bg rounded-ds-default p-4 mb-6">
            <a
              href="mailto:support@directhomeservice.com"
              className="text-primary hover:underline font-medium"
            >
              support@directhomeservice.com
            </a>
          </div>

          {/* Retry Button */}
          <button
            onClick={handleManualRetry}
            disabled={isChecking}
            className="w-full bg-primary text-white py-3 px-6 rounded-ds-default font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isChecking ? t('checking') || 'Checking...' : t('tryAgain') || 'Try Again'}
          </button>
        </div>

        {/* Footer */}
        <p className="mt-8 text-sm text-ds-text-tertiary">
          &copy; {new Date().getFullYear()} {t('directHomeService')}
        </p>
      </div>
    </div>
  );
};

export default ServiceUnavailable;
